import { MultiSelectableOptions } from "client/Hooks";
import { ReactElement, useCallback } from "react";
import {
	Column,
	ColumnState,
	ExtensionColumn,
	SelectColumn
} from "./Column";
import {
	TableViewColumnDefinition,
	convertToColumnSizeDefinition,
	convertToColumnState
} from "./TableView";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import styles from "./TableView.module.less";

export type HeaderProps = {
	multiSelectable: MultiSelectableOptions | false;
	columnsState: readonly ColumnState[];
	onColumnsStateChange(columnsState: readonly ColumnState[]): void;
	columnDefinitions: readonly TableViewColumnDefinition[];
	extension?: ReactElement;
	backgroundColor: string | undefined;
};

export const Header = (props: HeaderProps): ReactElement => {
	const {
		multiSelectable,
		columnsState,
		onColumnsStateChange,
		columnDefinitions,
		extension,
		backgroundColor
	} = props;

	const columnStateChangeHandler = useCallback(
		(columnState: ColumnState, index: number): void => {
			const copy: ColumnState[] = [...columnsState];

			copy.splice(index, 1, columnState);

			onColumnsStateChange(copy);
		},
		[columnsState, onColumnsStateChange]
	);

	const getColumnState = (column: TableViewColumnDefinition): ColumnState => {
		const existingState: ColumnState | undefined = columnsState.find(s => s.id === column.trackingId);

		if (isNotNil(existingState)) {
			return existingState;
		}

		return convertToColumnState(
			convertToColumnSizeDefinition(column)
		);
	};

	return (
		<div
			{...setTrackingId("header")}
			className={styles.header}
		>
			{multiSelectable && (
				<SelectColumn
					multiSelectable={multiSelectable}
					backgroundColor={backgroundColor}
				/>
			)}
			{columnDefinitions.map((c, i) => (
				<Column
					key={i}
					columnState={getColumnState(c)}
					onChangeColumnState={columnStateChangeHandler}
					index={i}
					sortable={c.sortable ?? false}
					filterable={c.filterable ?? false}
					isResizable={c.resizable ?? true}
					trackingId={c.trackingId}
					backgroundColor={backgroundColor}
				>
					{c.title}
				</Column>
			))}
			<ExtensionColumn backgroundColor={backgroundColor}>
				{extension}
			</ExtensionColumn>
		</div>
	);
};
Header.displayName = "Header";